import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import Article from "./Article";
import { formatForDisplay } from './utils';
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";

function buildPaymentsUrl(path, filters, page_size, lastId) {
    const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
    );

    cleanedFilters.limit = page_size;

    if (lastId) {
        cleanedFilters.last_id = lastId;
    }

    const queryString = new URLSearchParams(cleanedFilters).toString();
    return `/${path}?${queryString}`;
}

function Payments({ statsData, onViewDetails }) {
    const [lastId, setLastId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({
        wa_id: "",
        policy_number: "",
        reference: "",
        status: "success",
        method: "",
        wallet_id: "",
        min_date: "",
        max_date: ""
    });
    const observer = useRef();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const PAGE_SIZE = 10;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url = buildPaymentsUrl('payments', filters, PAGE_SIZE, lastId);
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            setPayments(prevItems => [...prevItems, ...response.data]);
            setHasMore(response.data.length === PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, auth.accessToken, lastId, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const location = useLocation();
    const navigate = useNavigate();

    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setLastId(node.id);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const selectedArticle = statsData.find(
        article => article.link === location.pathname
    );

    const selectedArticleRef = useRef(null);

    const downloadExcel = async () => {
        try {
            let url = buildPaymentsUrl('payments_csv', filters, PAGE_SIZE, lastId);
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            const href = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", 'payments.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch(err) {
            console.log(err);
        }
    };

    const updateFilters = (evt) => {
        const {name, value} = evt.target;
        setFilters(curr => ({...curr, [name]: value}));
        setLastId(null);
        setPayments([]);
    }

    return (
        <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4">
                <div className="flex overflow-x-auto md:overflow-x-hidden md:overflow-y-auto md:max-h-screen md:h-screen md:pb-32 md:pr-4 md:grid md:grid-cols-1 gap-6 pb-4 md:pb-0">
                    {statsData.map((article, index) => (
                        <Article
                            key={index}
                            title={article.title}
                            value={article.value}
                            isObject={article.isObject}
                            isSelected={selectedArticle.title === article.title}
                            onViewDetails={() => onViewDetails(article)}
                            ref={selectedArticle.title === article.title ? selectedArticleRef : null}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full md:w-3/4">
                <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                    <h2 className="text-2xl font-medium text-gray-900">{selectedArticle.title}</h2>
                    <p className="text-sm text-gray-500"></p>
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-800">Payments</h3>
                                <button
                                    onClick={downloadExcel}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    Download Excel
                                </button>
                                <div className="flex flex-wrap gap-4 mt-2">
                                    <div className="w-full md:w-auto">
                                        <label className="block text-sm font-medium text-gray-700">Search Whatsapp Number</label>
                                        <input
                                            type="text"
                                            name="wa_id"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.wa_id}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div className="w-full md:w-auto">
                                        <label className="block text-sm font-medium text-gray-700">Search Wallet Number</label>
                                        <input
                                            type="text"
                                            name="wallet_id"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.wallet_id}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div className="w-full md:w-auto">
                                        <label className="block text-sm font-medium text-gray-700">Reference</label>
                                        <input
                                            type="text"
                                            name="reference"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.reference}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Filter by Status</label>
                                        <select
                                            name="status"
                                            className="mt-1 p-2 border border-gray-300 rounded-md"
                                            value={filters.status}
                                            onChange={updateFilters}
                                        >
                                            <option value="">Select Status</option>
                                            <option value="success">Success</option>
                                            <option value="pending">Pending</option>
                                            <option value="failed">Failed</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Filter by Status</label>
                                        <select
                                            name="method"
                                            className="mt-1 p-2 border border-gray-300 rounded-md"
                                            value={filters.method}
                                            onChange={updateFilters}
                                        >
                                            <option value="">Select Payment Method</option>
                                            <option value="ecocash">Ecocash</option>
                                            <option value="paynow">Paynow</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Policy Number</label>
                                        <input
                                            type="text"
                                            name="policy_number"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.policy_number}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Min Date</label>
                                        <input
                                            type="datetime-local"
                                            name="min_date"
                                            className="mt-1 p-2 border border-gray-300 rounded-md"
                                            value={filters.min_date}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Max Date</label>
                                        <input
                                            type="datetime-local"
                                            name="max_date"
                                            className="mt-1 p-2 border border-gray-300 rounded-md"
                                            value={filters.max_date}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {payments && payments.length > 0 ? (
                            <div className="overflow-hidden">
                                <div className="max-h-80 overflow-y-auto">
                                    <table className="min-w-full">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    #
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Payment Method
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Voucher
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Agent ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    MM Wallet
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Amount
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    DateTime
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Policy Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Phone
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    WhatsApp Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Reference
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {payments.map((payment, index) => {
                                                if (payments.length === index + 1) {
                                                    return (
                                                        <tr id={payment.id} ref={lastItemRef} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.payment_method}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.voucher}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.agent_id}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.wallet}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.amount}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.status}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.created_at}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.policy_number}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.wallet_id}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatForDisplay(payment.number.wa_id)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.reference}
                                                            </td>
                                                        </tr>
                                                    )
                                                } else {
                                                    return (
                                                        <tr id={payment.id} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.payment_method}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.voucher}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.agent_id}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.wallet}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.amount}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {payment.status}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.created_at}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.policy_number}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.wallet_id}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatForDisplay(payment.number.wa_id)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {payment.reference}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">No payments found</div>
                        )}
                    </div>
                    <button onClick={() => navigate("/dashboard")} className="mt-4 inline-block text-blue-600 hover:underline">
                        ← Back to List
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Payments;